import React, { useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
  Avatar,
  Switch,
} from "@mui/material";
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Collections as CollectionsIcon,
  CardGiftcard as GifBoxIcon,
  Person2 as UsersIcon,
  ArrowBack as ArrowBackIcon,
  Logout as LogoutIcon,
  Edit as EditIcon,
  Chat as ChatIcon,
  NewspaperOutlined as PostsIcon,
  AccountBalanceOutlined as BankAccountsIcon,
  GamesOutlined as GamesIcon,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import UserDetailsSection from "../../Profile/UserDetailsSection";
import SecuritySection from "../../Profile/SecuritySection";
import NFTCollectionsSection from "../../Profile/NFTCollectionsSection";
import GiftsSection from "../../Profile/GiftsSection";
import ContactsSection from "../../Profile/ContactsSection";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { logout } from "../../../redux/authSlice"; // Assuming you have a logout action
import {
  setCurrentNavigationView,
  setSelectedChatView,
  setMessagingEnabled,
} from "../../../redux/uiSlice";
import { setSelectedSettingsTab } from "../../../redux/uiSlice";
import { fetchNftCollections } from "../../../redux/nftSlice";
import {
  getUserInfo,
  setUserMessagingPreferences,
} from "../../../redux/userSlice";
import CowIcon from "../../CowIcon";
import PostsSection from "../../Profile/PostsSection";
import GamingSection from "../../Profile/GamingSection";
import PersonalAccessTokenManagement from "../../Profile/PersonalAccessTokenManagement";

const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500",
);

const SettingsView: React.FC = () => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const { selectedSettingsTab } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const allowedUsers = [
    "0xca3452e54C12ba655dacaEec2D94a0e1d987e605",
    "0xFC1bbF7d79c5e0b9abCd1451615B6Af5D9Fe7108",
  ];
  const { chainType } = useAppSelector((state) => state.wallet);
  const showPostsListItem = user && allowedUsers.includes(user.walletAddress);

  const handleMessagingToggle = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setMessagingEnabled(event.target.checked));
    dispatch(
      setUserMessagingPreferences({ enableMessaging: event.target.checked }),
    );
  };

  const renderSectionContent = () => {
    switch (selectedSettingsTab) {
      case "details":
        return <UserDetailsSection />;
      case "security":
        return <SecuritySection />;
      case "nfts":
        return <NFTCollectionsSection />;
      case "gifts":
        return <GiftsSection />;
      case "contacts":
        return <ContactsSection />;
      case "bank_accounts":
        return <ContactsSection />;
      case "gaming":
        return <GamingSection />;
      case "accessTokens":
        return <PersonalAccessTokenManagement />;

      case "posts":
        return <PostsSection />;
      case "general":
        return (
          <>
            <List sx={{ width: "100%", mb: 2, p: isMobileView ? 1 : 2 }}>
              <ListItem disablePadding>
                <ListItemButton sx={{ py: 1.5, px: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Avatar sx={{ width: 40, height: 40, mr: 2 }}>
                      {user?.userName.charAt(0)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {user?.fullName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user?.emailAddress}
                      </Typography>
                    </Box>
                    {/* <EditIcon fontSize="small" color="action" /> */}
                  </Box>
                </ListItemButton>
              </ListItem>
            </List>

            {/* <Divider sx={{ my: 1 }} /> */}
            <List sx={{ p: isMobileView ? 0 : 1 }}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => dispatch(setSelectedSettingsTab("details"))}
                  sx={{ py: isMobileView ? 1 : 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: isMobileView ? 40 : 56 }}>
                    <PersonIcon fontSize={isMobileView ? "small" : "medium"} />
                  </ListItemIcon>
                  <ListItemText
                    primary="User Details"
                    primaryTypographyProps={{
                      fontSize: isMobileView ? "0.875rem" : "1rem",
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => dispatch(setSelectedSettingsTab("security"))}
                  sx={{ py: isMobileView ? 1 : 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: isMobileView ? 40 : 56 }}>
                    <SecurityIcon
                      fontSize={isMobileView ? "small" : "medium"}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Security"
                    primaryTypographyProps={{
                      fontSize: isMobileView ? "0.875rem" : "1rem",
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => dispatch(setSelectedSettingsTab("contacts"))}
                >
                  <ListItemIcon>
                    <UsersIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Contacts"
                    primaryTypographyProps={{
                      fontSize: isMobileView ? "0.875rem" : "1rem",
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {chainType !== "evm" && (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() =>
                      dispatch(setSelectedSettingsTab("bank_accounts"))
                    }
                  >
                    <ListItemIcon>
                      <BankAccountsIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Linked Accounts"
                      primaryTypographyProps={{
                        fontSize: isMobileView ? "0.875rem" : "1rem",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {showPostsListItem && (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => dispatch(setSelectedSettingsTab("posts"))}
                  >
                    <ListItemIcon>
                      <PostsIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Posts"
                      primaryTypographyProps={{
                        fontSize: isMobileView ? "0.875rem" : "1rem",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {chainType === "evm" && (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      dispatch(setSelectedSettingsTab("nfts"));
                      dispatch(fetchNftCollections());
                    }}
                  >
                    <ListItemIcon>
                      <CollectionsIcon
                        fontSize={isMobileView ? "small" : "medium"}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="NFT Collections"
                      primaryTypographyProps={{
                        fontSize: isMobileView ? "0.875rem" : "1rem",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => dispatch(setSelectedSettingsTab("gifts"))}
                >
                  <ListItemIcon>
                    <GifBoxIcon fontSize={isMobileView ? "small" : "medium"} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Gifts"
                    primaryTypographyProps={{
                      fontSize: isMobileView ? "0.875rem" : "1rem",
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => dispatch(setSelectedSettingsTab("gaming"))}
                  sx={{ py: isMobileView ? 1 : 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: isMobileView ? 40 : 56 }}>
                    <GamesIcon fontSize={isMobileView ? "small" : "medium"} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Gaming"
                    primaryTypographyProps={{
                      fontSize: isMobileView ? "0.875rem" : "1rem",
                    }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  onClick={() =>
                    dispatch(setSelectedSettingsTab("accessTokens"))
                  }
                >
                  <ListItemIcon>
                    <VpnKeyIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Access Tokens"
                    primaryTypographyProps={{
                      fontSize: isMobileView ? "0.875rem" : "1rem",
                    }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    dispatch(logout());
                    dispatch(setCurrentNavigationView("chat"));
                    dispatch(setSelectedChatView("list"));
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize={isMobileView ? "small" : "medium"} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Log out"
                    primaryTypographyProps={{
                      fontSize: isMobileView ? "0.875rem" : "1rem",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        );
    }
  };

  const getSectionTitle = () => {
    switch (selectedSettingsTab) {
      case "details":
        return "User Details";
      case "security":
        return "Security Settings";
      case "nfts":
        return "NFT Collections";
      case "gifts":
        return "Gift Cards";
      case "contacts":
        return "Contacts";
      case "accessTokens":
        return "Access Tokens";
      default:
        return "Settings";
    }
  };

  const handleBack = () => {
    dispatch(setSelectedSettingsTab("general")); // Reset to general settings
  };

  const renderHeader = () => (
    <>
      <Box sx={{ p: 2, textAlign: "start" }}>
        <Typography
          variant="h4"
          sx={{ fontSize: isMobileView ? "1.5rem" : "2rem" }}
        >
          Settings
        </Typography>
        <Typography sx={{ fontSize: isMobileView ? "0.875rem" : "1rem" }}>
          Manage your account settings and preferences.
        </Typography>
      </Box>

      <Divider />
    </>
  );

  useEffect(() => {
    const userInfo = async () => {
      await dispatch(getUserInfo());
    };

    userInfo();
  }, [dispatch]);

  return (
    <Paper
      sx={{
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        height: isMobileView ? "700px" : "850px",
        overflow: "auto",
        // Mobile styles
        ...(isMobileView && {
          maxWidth: "100%",
          borderRadius: 0,
          boxShadow: "none",
        }),
        // Desktop styles
        ...(!isMobileView && {
          borderRadius: 2,
          boxShadow: 3,
        }),
      }}
    >
      {selectedSettingsTab && (
        <>
          {renderHeader()}
          {selectedSettingsTab !== "general" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: isMobileView ? 1 : 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <IconButton
                onClick={handleBack}
                sx={{ mr: 1 }}
                size={isMobileView ? "small" : "medium"}
              >
                <ArrowBackIcon fontSize={isMobileView ? "small" : "medium"} />
              </IconButton>
              <Typography
                variant="h6"
                sx={{ fontSize: isMobileView ? "1.25rem" : "1.5rem" }}
              >
                {getSectionTitle()}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              width: "100%",
              height: "calc(100% - 120px)",
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {renderSectionContent()}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default SettingsView;
